import React from 'react';
import { useIntl } from 'react-intl';
import { LanguageContext } from '../contexts/LanguageContext';
import SectionLayout from '../components/SectionLayout';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Textarea from '../components/ui/Textarea';
import { useContactForm } from '../hooks/useContactForm';
import { CONTACT_FORM_CONFIG } from '../config/constants';

export default function Contact() {
  const intl = useIntl();
  const { locale } = React.useContext(LanguageContext);
  const { isSubmitting, submitStatus, submitForm } = useContactForm();
  const [selectedInquiryType, setSelectedInquiryType] = React.useState('');
  const isContactFormDisabled = !CONTACT_FORM_CONFIG.IS_ENABLED;

  const handleSubmit = async e => {
    e.preventDefault();

    // オリパに関するお問い合わせが選択されている場合は送信を防ぐ
    if (isOlpaInquiry) {
      return;
    }

    const formData = new FormData(e.target);
    const result = await submitForm(formData);

    if (result.success) {
      e.target.reset(); // フォームをリセット
      setSelectedInquiryType(''); // 選択状態もリセット
    }
  };

  // 日本語のみでオリパの選択肢を表示
  const baseInquiryTypes = [
    'contact.form.inquiryType1',
    'contact.form.inquiryType2',
    'contact.form.inquiryType3',
  ];

  const inquiryTypes =
    locale === 'ja'
      ? [
          ...baseInquiryTypes,
          'contact.form.inquiryType4',
          'contact.form.inquiryType5',
        ]
      : [...baseInquiryTypes, 'contact.form.inquiryType5'];

  const handleInquiryTypeChange = e => {
    setSelectedInquiryType(e.target.value);
  };

  const isOlpaInquiry =
    selectedInquiryType ===
    intl.formatMessage({ id: 'contact.form.inquiryType4' });

  // LINEサポートリンク（日本語のみで使用）
  const getSupportLink = () => {
    return 'https://lin.ee/3ULbL6E';
  };

  return (
    <SectionLayout
      accent="secondary"
      background="white"
      className="!pb-[120px]"
    >
      <div>
        {/* ヘッダー */}
        <h2
          className="section-title animate-slideInLeft"
          style={{ animationDelay: '0.3s' }}
        >
          {intl.formatMessage({ id: 'contact.title' })}
        </h2>
        <p
          className="section-subtitle animate-slideInLeft text-muted-foreground"
          style={{ animationDelay: '0.5s' }}
        >
          {intl.formatMessage({ id: 'contact.subtitle' })}
        </p>

        {/* 説明 */}
        <div
          className="mb-12 animate-fadeInUp"
          style={{ animationDelay: '0.9s' }}
        >
          <p className="text-lg text-muted-foreground mb-6 font-body leading-loose">
            {intl.formatMessage({ id: 'contact.description1' })}
          </p>
          <div className="inline-block bg-muted rounded-xl px-6 py-3 border border-border">
            <p className="text-sm text-muted-foreground leading-loose">
              {intl.formatMessage({ id: 'contact.description2' })}
              <a
                href="https://lin.ee/3ULbL6E"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-bold mx-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {intl.formatMessage({ id: 'contact.line_support' })}
              </a>
              {intl.formatMessage({ id: 'contact.line_support_suffix' })}
            </p>
          </div>
        </div>

        {/* フォームカード */}
        <div
          className="bg-card rounded-2xl px-5 py-8 md:p-10 shadow-card border border-border animate-floatUp"
          style={{ animationDelay: '1.0s' }}
        >
          <form className="space-y-8" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-muted-foreground mb-2 font-body"
                >
                  {intl.formatMessage({ id: 'contact.form.name' })}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  placeholder={intl.formatMessage({ id: 'contact.form.name' })}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-muted-foreground mb-2 font-body"
                >
                  {intl.formatMessage({ id: 'contact.form.email' })}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={intl.formatMessage({ id: 'contact.form.email' })}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <p className="block text-sm font-semibold text-muted-foreground mb-4 font-body">
                {intl.formatMessage({ id: 'contact.form.inquiryType' })}
                <span className="text-red-500 ml-1">*</span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {inquiryTypes.map((typeId, index) => (
                  <label
                    key={index}
                    className={`flex items-center p-4 rounded-xl border cursor-pointer transition-all duration-200
                      ${
                        selectedInquiryType ===
                        intl.formatMessage({ id: typeId })
                          ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                          : 'border-border bg-muted hover:border-muted-foreground/30'
                      }`}
                  >
                    <input
                      type="radio"
                      name="inquiry_type"
                      value={intl.formatMessage({ id: typeId })}
                      className="form-radio h-4 w-4 text-primary border-input focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-ring bg-transparent"
                      required
                      disabled={isSubmitting}
                      onChange={handleInquiryTypeChange}
                    />
                    <span className="ml-3 text-sm text-muted-foreground font-body leading-snug">
                      {intl.formatMessage({ id: typeId })}
                    </span>
                  </label>
                ))}
              </div>

              {/* オリパに関するお問い合わせ選択時の導線 */}
              {isOlpaInquiry && (
                <div className="mt-4 p-4 bg-primary-50 border border-primary-200 rounded-xl flex items-center justify-between flex-wrap gap-4">
                  <p className="text-foreground font-body text-sm">
                    {intl.formatMessage({ id: 'contact.form.olpa_redirect' })}
                  </p>
                  <a
                    href={getSupportLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors bg-green-500 hover:bg-green-600 shadow-sm shadow-green-200 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {intl.formatMessage({
                      id: 'contact.form.olpa_redirect_button',
                    })}
                  </a>
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-sm font-medium text-muted-foreground mb-2 font-body"
              >
                {intl.formatMessage({ id: 'contact.form.message' })}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <Textarea
                id="message"
                name="message"
                rows="6"
                placeholder={intl.formatMessage({ id: 'contact.form.message' })}
                required
                disabled={isSubmitting}
              />
            </div>

            <div role="status" aria-live="polite" aria-atomic="true">
              {isSubmitting && (
                <span className="sr-only">
                  {intl.formatMessage({ id: 'contact.form.submitting' })}
                </span>
              )}
              {/* 送信状態の表示 */}
              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <p className="text-green-700 font-body text-sm font-medium">
                    {intl.formatMessage({ id: 'contact.form.success' })}
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <p className="text-red-700 font-body text-sm font-medium">
                    {intl.formatMessage({ id: 'contact.form.error' })}
                  </p>
                </div>
              )}

              {submitStatus === 'bot_detected' && (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                  <p className="text-orange-700 font-body text-sm font-medium">
                    {intl.formatMessage({ id: 'contact.form.bot_detected' })}
                  </p>
                </div>
              )}
            </div>

            {isContactFormDisabled && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                <p className="text-amber-800 font-body text-sm font-medium">
                  {intl.formatMessage({
                    id: 'contact.form.disabled',
                    defaultMessage:
                      'In review environments, the contact form does not send inquiries.',
                  })}
                </p>
              </div>
            )}

            <div className="pt-4">
              <Button
                type="submit"
                className="px-8 py-3 font-body w-full sm:w-auto min-w-[200px] text-center"
                disabled={
                  isSubmitting || isOlpaInquiry || isContactFormDisabled
                }
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {intl.formatMessage({ id: 'contact.form.submitting' })}
                  </>
                ) : (
                  intl.formatMessage({ id: 'contact.form.submit' })
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </SectionLayout>
  );
}
